import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { FlexBox, StickyBox } from 'components/Box'
import { SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { AbTestsAddForm } from 'features/ab-tests/forms/AbTestsAddForm'
import { cannotEditAbTest } from 'features/ab-tests/utils'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'

import type { AbTestsAddEditContextType } from 'features/ab-tests/contexts/AbTestsAddEditContext'

export const AbTestsAdd = (): JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { t } = useTranslation('features/abTests')
  const [canSaveAndStart, setCanSaveAndStart] = useState<boolean>(false)

  const formId: string = 'ab-tests-form-add'
  const contextProps: AbTestsAddEditContextType = {
    ...useCommonFormContextProps({
      formId,
    }),
    canSaveAndStart,
    isDisabled: cannotEditAbTest('add', ability, undefined),
    setCanSaveAndStart,
  }

  const DraftStatusButtons = ({
    ctxProps,
  }: {
    ctxProps: AbTestsAddEditContextType
  }): JSX.Element => {
    const canCreate: boolean = ability.can('create', 'AbTests')

    return (
      <FlexBox
        axis='x'
        data-product-fruits={generateSelector({ step: 8, subTour: 2, tour: 5 })}
      >
        <SaveButton
          {...ctxProps}
          can={canCreate}
          id={`${formId}-save-as-draft-button`}
          text={t('form.action.saveAsDraft')}
          variant='text'
        />
        <SaveButton
          {...ctxProps}
          can={canCreate && ctxProps.canSaveAndStart}
          id={`${formId}-save-and-start-button`}
          text={t('form.action.saveAndStart')}
          tooltip={
            !ctxProps.canSaveAndStart
              ? { content: t('form.tooltip.activeTestOnDomain') }
              : undefined
          }
        />
      </FlexBox>
    )
  }

  return (
    <AbTestsAddEditContext.Provider value={contextProps}>
      <StickyBox>
        <AbTestsAddEditContext.Consumer>
          {(ctxProps: AbTestsAddEditContextType): JSX.Element => (
            <PageHeader
              actions={<DraftStatusButtons ctxProps={ctxProps} />}
              backTo={generatePath(ROUTES.AB_TESTS.LIST, { workspaceId })}
              title={t('common.header.title')}
            />
          )}
        </AbTestsAddEditContext.Consumer>
      </StickyBox>

      <AbTestsAddForm />
    </AbTestsAddEditContext.Provider>
  )
}
